/**
 * 主页 - Monaco Editor 代码编辑器
 *
 * 提供功能完整的在线代码编辑器，支持多种编程语言和主题
 */

import { useCallback, useEffect, useState } from 'react'
import { LanguageSelector, LocalizedMonacoEditor, ThemeToggle } from '@/components/monaco-editor'
import type { SupportedLanguage } from '@/types/monaco-editor'
import './styles.css'

// 示例代码模板
const CODE_TEMPLATES: Record<SupportedLanguage, string> = {
  typescript: `// TypeScript 示例
interface User {
  id: number;
  name: string;
  email: string;
}

class UserService {
  private users: User[] = [];

  constructor() {
    this.loadUsers();
  }

  private loadUsers(): void {
    // 模拟从API加载用户数据
    this.users = [
      { id: 1, name: "Alice", email: "alice@example.com" },
      { id: 2, name: "Bob", email: "bob@example.com" }
    ];
  }

  public getUserById(id: number): User | undefined {
    return this.users.find(user => user.id === id);
  }

  public getAllUsers(): User[] {
    return [...this.users];
  }
}

// 使用示例
const userService = new UserService();
const user = userService.getUserById(1);
console.log(user?.name); // 输出: Alice`,

  javascript: `// JavaScript 示例 - 异步数据处理
class DataProcessor {
  constructor() {
    this.cache = new Map();
  }

  // 异步获取数据
  async fetchData(url) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(\`HTTP error! status: \${response.status}\`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching data:', error);
      return null;
    }
  }

  // 带缓存的数据处理
  async processData(url) {
    if (this.cache.has(url)) {
      console.log('从缓存返回数据');
      return this.cache.get(url);
    }

    const data = await this.fetchData(url);
    if (data) {
      // 数据转换处理
      const processedData = this.transformData(data);
      this.cache.set(url, processedData);
      return processedData;
    }
    return null;
  }

  transformData(data) {
    // 示例数据转换
    return {
      count: data.length,
      items: data.map(item => ({
        ...item,
        processed: true
      }))
    };
  }
}`,

  python: `# Python 示例 - 数据处理和分析
import json
import statistics
from datetime import datetime
from typing import List, Dict, Any

class DataAnalyzer:
    def __init__(self, data_source: str):
        self.data_source = data_source
        self.data = []

    def load_data(self) -> bool:
        """从JSON文件加载数据"""
        try:
            with open(self.data_source, 'r', encoding='utf-8') as f:
                self.data = json.load(f)
            print(f"成功加载 {len(self.data)} 条数据")
            return True
        except FileNotFoundError:
            print(f"文件 {self.data_source} 不存在")
            return False
        except json.JSONDecodeError:
            print(f"JSON格式错误")
            return False

    def analyze_numbers(self, field: str) -> Dict[str, float]:
        """分析数值字段"""
        numbers = [item[field] for item in self.data if isinstance(item[field], (int, float))]

        if not numbers:
            return {}

        return {
            'count': len(numbers),
            'mean': statistics.mean(numbers),
            'median': statistics.median(numbers),
            'min': min(numbers),
            'max': max(numbers),
            'std_dev': statistics.stdev(numbers) if len(numbers) > 1 else 0
        }

    def get_summary(self) -> Dict[str, Any]:
        """获取数据摘要"""
        return {
            'total_records': len(self.data),
            'fields': list(self.data[0].keys()) if self.data else [],
            'last_updated': datetime.now().isoformat()
        }

# 使用示例
if __name__ == "__main__":
    analyzer = DataAnalyzer("data.json")
    analyzer.load_data()
    print(analyzer.get_summary())`,

  html: `<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Monaco Editor 示例</title>
    <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
</head>
<body class="bg-gray-100 min-h-screen">
    <div class="container mx-auto px-4 py-8">
        <header class="bg-white shadow rounded-lg p-6 mb-6">
            <h1 class="text-3xl font-bold text-gray-800 mb-2">Monaco Editor 演示</h1>
            <p class="text-gray-600">功能强大的在线代码编辑器</p>
        </header>

        <main class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section class="bg-white rounded-lg shadow p-6">
                <h2 class="text-xl font-semibold mb-4 text-gray-800">功能特性</h2>
                <ul class="space-y-2 text-gray-600">
                    <li class="flex items-center">
                        <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        语法高亮和智能提示
                    </li>
                    <li class="flex items-center">
                        <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        多语言支持
                    </li>
                    <li class="flex items-center">
                        <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        深色/浅色主题
                    </li>
                </ul>
            </section>

            <section class="bg-white rounded-lg shadow p-6">
                <h2 class="text-xl font-semibold mb-4 text-gray-800">支持的语言</h2>
                <div class="grid grid-cols-2 gap-2 text-sm">
                    <div class="flex items-center text-gray-600">
                        <span class="text-blue-500 mr-2">📘</span> TypeScript
                    </div>
                    <div class="flex items-center text-gray-600">
                        <span class="text-yellow-500 mr-2">⚡</span> JavaScript
                    </div>
                    <div class="flex items-center text-gray-600">
                        <span class="text-green-500 mr-2">🐍</span> Python
                    </div>
                    <div class="flex items-center text-gray-600">
                        <span class="text-red-500 mr-2">☕</span> Java
                    </div>
                </div>
            </section>
        </main>
    </div>

    <script>
        // 页面交互逻辑
        document.addEventListener('DOMContentLoaded', function() {
            console.log('Monaco Editor Demo loaded');
        });
    </script>
</body>
</html>`,

  css: `/* CSS 示例 - 现代化样式设计 */
:root {
  /* 主色调 */
  --primary-color: #3b82f6;
  --primary-hover: #2563eb;
  --primary-light: #eff6ff;

  /* 辅助色 */
  --success-color: #10b981;
  --warning-color: #f59e0b;
  --error-color: #ef4444;

  /* 中性色 */
  --gray-50: #f9fafb;
  --gray-100: #f3f4f6;
  --gray-900: #111827;

  /* 阴影 */
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

/* 基础重置 */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  line-height: 1.6;
  color: var(--gray-900);
  background-color: var(--gray-50);
}

/* 响应式网格系统 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.grid {
  display: grid;
  gap: 1rem;
}

.grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
.grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }

@media (max-width: 768px) {
  .grid-cols-2,
  .grid-cols-3 {
    grid-template-columns: 1fr;
  }
}

/* 卡片组件 */
.card {
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  transition: all 0.2s ease-in-out;
}

.card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

/* 按钮组件 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background-color: var(--primary-hover);
  transform: translateY(-1px);
}`,

  json: `{
  "project": {
    "name": "Monaco Editor Demo",
    "version": "1.0.0",
    "description": "功能完整的在线代码编辑器",
    "author": {
      "name": "Developer",
      "email": "dev@example.com"
    }
  },
  "features": [
    {
      "id": "syntax-highlighting",
      "name": "语法高亮",
      "enabled": true,
      "languages": ["typescript", "javascript", "python", "java", "html", "css", "json"]
    },
    {
      "id": "intellisense",
      "name": "智能提示",
      "enabled": true,
      "types": ["completion", "signature", "hover"]
    },
    {
      "id": "themes",
      "name": "主题系统",
      "enabled": true,
      "themes": [
        {
          "id": "tailwind-dark",
          "name": "Tailwind Dark",
          "type": "dark"
        },
        {
          "id": "tailwind-light",
          "name": "Tailwind Light",
          "type": "light"
        }
      ]
    }
  ],
  "statistics": {
    "total_languages": 25,
    "supported_features": 15,
    "user_count": 1000
  }
}`,

  markdown: `# Monaco Editor 代码编辑器

## 🚀 简介

Monaco Editor 是微软开发的代码编辑器，被广泛应用于 VS Code、GitHub 等平台。本应用基于 Monaco Editor 提供了一个功能完整的在线代码编辑体验。

## ✨ 主要功能

### 🎨 语法高亮
- 支持 **25+** 种编程语言
- 智能语法分析
- 错误高亮提示

### 🧠 智能提示
- 代码自动完成
- 函数签名提示
- 悬浮信息显示

### 🎭 主题系统
- **Tailwind Dark** - 深色主题，保护眼睛
- **Tailwind Light** - 浅色主题，清晰明亮
- 自动跟随系统主题

### ⚡ 高性能
- **Web Worker** 多线程处理
- 按需加载语言包
- 代码分割优化

## 🛠️ 支持的语言

| 类别 | 语言 |
|------|------|
| **Web开发** | HTML, CSS, JavaScript, TypeScript |
| **后端开发** | Python, Java, Go, Rust, C# |
| **数据格式** | JSON, YAML, XML, Markdown |
| **脚本语言** | Bash, PowerShell, Ruby, Perl |

## 💡 使用技巧

### 快捷键
- \`Ctrl/Cmd + S\` - 保存代码
- \`Ctrl/Cmd + F\` - 查找
- \`Ctrl/Cmd + H\` - 替换
- \`Ctrl/Cmd + /\` - 切换注释

### 代码示例
\`\`\`typescript
// TypeScript 示例
function greet(name: string): string {
  return \`Hello, \${name}!\`;
}

console.log(greet("World"));
\`\`\`

## 🔧 技术栈

- **React 19** - 用户界面框架
- **TypeScript 5** - 类型安全
- **TailwindCSS 4** - 样式框架
- **Rsbuild** - 构建工具
- **Monaco Editor** - 代码编辑引擎

---

> 📝 提示：点击右上角的语言选择器可以切换不同的编程语言，点击主题按钮可以切换深色/浅色模式。`,

  sql: `-- SQL 示例 - 数据库查询和分析
-- 创建用户表
CREATE TABLE users (
    id INT PRIMARY KEY AUTO_INCREMENT,
    username VARCHAR(50) UNIQUE NOT NULL,
    email VARCHAR(100) UNIQUE NOT NULL,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP ON UPDATE CURRENT_TIMESTAMP
);

-- 创建订单表
CREATE TABLE orders (
    id INT PRIMARY KEY AUTO_INCREMENT,
    user_id INT NOT NULL,
    product_name VARCHAR(200) NOT NULL,
    quantity INT DEFAULT 1,
    price DECIMAL(10, 2) NOT NULL,
    order_date TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    FOREIGN KEY (user_id) REFERENCES users(id)
);

-- 插入示例数据
INSERT INTO users (username, email) VALUES
('alice', 'alice@example.com'),
('bob', 'bob@example.com'),
('charlie', 'charlie@example.com');

INSERT INTO orders (user_id, product_name, quantity, price) VALUES
(1, 'Laptop', 1, 999.99),
(1, 'Mouse', 2, 25.50),
(2, 'Keyboard', 1, 89.99),
(3, 'Monitor', 2, 299.99);

-- 复杂查询示例
-- 查询每个用户的订单统计
SELECT
    u.id,
    u.username,
    u.email,
    COUNT(o.id) as order_count,
    SUM(o.quantity * o.price) as total_spent,
    AVG(o.price) as avg_order_price,
    MAX(o.order_date) as last_order_date
FROM users u
LEFT JOIN orders o ON u.id = o.user_id
GROUP BY u.id, u.username, u.email
HAVING COUNT(o.id) > 0
ORDER BY total_spent DESC;

-- 查找高价值客户（消费超过1000）
SELECT
    u.username,
    u.email,
    SUM(o.quantity * o.price) as lifetime_value,
    COUNT(o.id) as order_count
FROM users u
JOIN orders o ON u.id = o.user_id
GROUP BY u.id, u.username, u.email
HAVING SUM(o.quantity * o.price) > 1000
ORDER BY lifetime_value DESC;`,

  yaml: `# 应用配置文件
app:
  name: "Monaco Editor Demo"
  version: "1.0.0"
  description: "功能完整的在线代码编辑器"
  environment: "production"

# 数据库配置
database:
  host: "localhost"
  port: 5432
  name: "monaco_editor_db"
  username: "admin"
  password: "your_password_here"
  pool:
    min: 5
    max: 20
    timeout: 30000

# 缓存配置
cache:
  type: "redis"
  host: "localhost"
  port: 6379
  ttl: 3600  # 1小时过期

# 编辑器配置
editor:
  default_language: "typescript"
  default_theme: "tailwind-dark"
  features:
    syntax_highlighting: true
    intellisense: true
    code_completion: true
    error_checking: true

  # 主题配置
  themes:
    tailwind-dark:
      background: "#1f2937"
      foreground: "#f9fafb"
      cursor: "#60a5fa"

    tailwind-light:
      background: "#ffffff"
      foreground: "#111827"
      cursor: "#2563eb"

# 语言支持配置
languages:
  - name: "TypeScript"
    extension: "ts"
    mime_type: "text/typescript"
    worker: true

  - name: "JavaScript"
    extension: "js"
    mime_type: "text/javascript"
    worker: true

  - name: "Python"
    extension: "py"
    mime_type: "text/x-python"
    worker: false

# 日志配置
logging:
  level: "info"
  format: "json"
  outputs:
    - type: "console"
      enabled: true
    - type: "file"
      path: "/var/log/app.log"
      max_size: "100MB"
      max_files: 10`,

  xml: `<?xml version="1.0" encoding="UTF-8"?>
<project xmlns="http://maven.apache.org/POM/4.0.0"
         xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
         xsi:schemaLocation="http://maven.apache.org/POM/4.0.0
         http://maven.apache.org/xsd/maven-4.0.0.xsd">

    <modelVersion>4.0.0</modelVersion>

    <groupId>com.example</groupId>
    <artifactId>monaco-editor-demo</artifactId>
    <version>1.0.0</version>
    <packaging>jar</packaging>

    <name>Monaco Editor Demo</name>
    <description>功能完整的在线代码编辑器演示项目</description>

    <properties>
        <maven.compiler.source>17</maven.compiler.source>
        <maven.compiler.target>17</maven.compiler.target>
        <project.build.sourceEncoding>UTF-8</project.build.sourceEncoding>
        <spring.boot.version>3.1.0</spring.boot.version>
    </properties>

    <dependencies>
        <!-- Spring Boot Starter -->
        <dependency>
            <groupId>org.springframework.boot</groupId>
            <artifactId>spring-boot-starter-web</artifactId>
            <version>3.1.0</version>
        </dependency>

        <!-- 数据库访问 -->
        <dependency>
            <groupId>org.springframework.boot</groupId>
            <artifactId>spring-boot-starter-data-jpa</artifactId>
            <version>3.1.0</version>
        </dependency>

        <!-- MySQL 驱动 -->
        <dependency>
            <groupId>mysql</groupId>
            <artifactId>mysql-connector-java</artifactId>
            <version>8.0.33</version>
        </dependency>

        <!-- JSON 处理 -->
        <dependency>
            <groupId>com.fasterxml.jackson.core</groupId>
            <artifactId>jackson-databind</artifactId>
            <version>2.15.2</version>
        </dependency>

        <!-- 工具库 -->
        <dependency>
            <groupId>org.apache.commons</groupId>
            <artifactId>commons-lang3</artifactId>
            <version>3.12.0</version>
        </dependency>
    </dependencies>

    <build>
        <plugins>
            <plugin>
                <groupId>org.springframework.boot</groupId>
                <artifactId>spring-boot-maven-plugin</artifactId>
                <version>3.1.0</version>
                <configuration>
                    <mainClass>com.example.MonacoEditorApplication</mainClass>
                </configuration>
            </plugin>
        </plugins>
    </build>
</project>`,

  // 其他语言的简单示例
  java: `// Java 示例
public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
    }
}`,

  go: `// Go 示例
package main

import "fmt"

func main() {
    fmt.Println("Hello, World!")
}`,

  rust: `// Rust 示例
fn main() {
    println!("Hello, World!");
}`,

  php: `<?php
// PHP 示例
echo "Hello, World!";
?>`,

  ruby: `# Ruby 示例
puts "Hello, World!"`,

  swift: `// Swift 示例
print("Hello, World!")`,

  kotlin: `// Kotlin 示例
fun main() {
    println("Hello, World!")
}`,

  csharp: `// C# 示例
using System;

class Program {
    static void Main() {
        Console.WriteLine("Hello, World!");
    }
}`,

  cpp: `// C++ 示例
#include <iostream>
using namespace std;

int main() {
    cout << "Hello, World!" << endl;
    return 0;
}`,

  bash: `#!/bin/bash
# Bash 脚本示例
echo "Hello, World!"`,

  powershell: `# PowerShell 示例
Write-Host "Hello, World!"`,

  dockerfile: `# Dockerfile 示例
FROM node:18-alpine
WORKDIR /app
COPY package*.json ./
RUN npm install
COPY . .
EXPOSE 3000
CMD ["npm", "start"]`,

  graphql: `# GraphQL 示例
type Query {
  hello: String!
}

type Mutation {
  setMessage(message: String!): String
}`,

  lua: `-- Lua 示例
print("Hello, World!")`,

  perl: `# Perl 示例
print "Hello, World!\n";`,

  r: `# R 示例
print("Hello, World!")`,

  scala: `// Scala 示例
object HelloWorld {
  def main(args: Array[String]): Unit = {
    println("Hello, World!")
  }
}`,

  vb: `' VB.NET 示例
Module Module1
    Sub Main()
        Console.WriteLine("Hello, World!")
    End Sub
End Module`,

  'objective-c': `// Objective-C 示例
#import <Foundation/Foundation.h>

int main(int argc, const char * argv[]) {
    @autoreleasepool {
        NSLog(@"Hello, World!");
    }
    return 0;
}`,

  dart: `// Dart 示例
void main() {
  print('Hello, World!');
}`,
}

/**
 * 主页组件
 */
export const HomePage = () => {
  const [code, setCode] = useState(CODE_TEMPLATES.typescript)
  const [language, setLanguage] = useState<SupportedLanguage>('typescript')
  const [theme, setTheme] = useState<'vs-dark' | 'vs-light'>('vs-dark')

  // 处理语言切换
  const handleLanguageChange = useCallback((newLanguage: SupportedLanguage) => {
    setLanguage(newLanguage)
    const template = CODE_TEMPLATES[newLanguage]
    if (template) {
      setCode(template)
    }
  }, [])

  // 处理代码变化
  const handleCodeChange = useCallback((value: string) => {
    setCode(value)
  }, [])

  // 处理主题切换
  const handleThemeChange = useCallback((newTheme: string) => {
    setTheme(newTheme as 'vs-dark' | 'vs-light')
  }, [])

  // 管理html元素的dark类
  useEffect(() => {
    const htmlElement = document.documentElement
    if (theme === 'vs-dark') {
      htmlElement.classList.add('dark')
    } else {
      htmlElement.classList.remove('dark')
    }
  }, [theme])

  return (
    <div className='monaco-playground min-h-screen bg-gray-50 dark:bg-gray-900'>
      {/* 头部工具栏 */}
      <header className='relative border-b border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 z-40'>
        <div className='container mx-auto px-4 py-4'>
          <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'>
            <div className='flex items-center gap-3'>
              <h1 className='text-xl sm:text-2xl font-bold text-gray-900 dark:text-white'>Monaco Editor</h1>
              <span className='px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded-full'>Demo</span>
            </div>

            <div className='flex flex-col sm:flex-row items-start sm:items-center gap-4'>
              <LanguageSelector
                value={language}
                onChange={handleLanguageChange}
                showSearch={true}
                showExtension={true}
                placeholder='选择编程语言...'
                size='md'
              />

              <ThemeToggle type='toggle' showLabel={false} size='md' onThemeChange={handleThemeChange} />
            </div>
          </div>

          {/* <div className='mt-3 flex items-center gap-2 text-sm text-gray-400 dark:text-gray-600'>
            <span>
              当前语言: <strong>{language}</strong>
            </span>
            <span>•</span>
            <span>
              字符数: <strong>{code.length.toLocaleString()}</strong>
            </span>
            <span>•</span>
            <span>
              行数: <strong>{code.split('\n').length}</strong>
            </span>
          </div> */}
        </div>
      </header>

      {/* 主编辑区域 */}
      <main className='h-[calc(100vh-8rem)]'>
        <LocalizedMonacoEditor
          value={code}
          language={language}
          theme={theme}
          onChange={handleCodeChange}
          height='100%'
          width='100%'
          locale='zh-cn'
          options={{
            fontSize: 14,
            fontFamily: 'JetBrains Mono, Fira Code, Monaco, Consolas, monospace',
            tabSize: 2,
            wordWrap: 'on',
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            lineNumbers: 'on',
            renderLineHighlight: 'line',
            occurrencesHighlight: true,
            codeLens: false,
            folding: true,
            foldingStrategy: 'indentation',
            showFoldingControls: 'mouseover',
            contextmenu: true,
            mouseWheelZoom: true,
            cursorBlinking: 'smooth',
            cursorSmoothCaretAnimation: true,
            bracketPairColorization: {
              enabled: true,
            },
            guides: {
              indentation: true,
              bracketPairs: true,
            },
          }}
        />
      </main>

      {/* 页脚信息 */}
      {/* <footer className='border-t border-gray-700 dark:border-gray-300 bg-gray-800 dark:bg-white py-3'>
        <div className='container mx-auto px-4'>
          <div className='flex flex-col sm:flex-row items-center justify-between text-sm text-gray-400 dark:text-gray-600'>
            <div className='flex items-center gap-4'>
              <span>基于 Monaco Editor 构建的在线代码编辑器</span>
            </div>
            <div className='flex items-center gap-4'>
              <span>支持 25+ 编程语言</span>
              <span>•</span>
              <span>智能代码提示</span>
              <span>•</span>
              <span>语法高亮</span>
            </div>
          </div>
        </div>
      </footer> */}
    </div>
  )
}
